import type { ReactNode } from 'react';

import type { TFunction } from 'i18next';
import {
  ArrowRightIcon,
  AtSignIcon,
  BanknoteIcon,
  BrainIcon,
  CheckCircle2Icon,
  CircleAlertIcon,
  ClockIcon,
  HistoryIcon,
  InfoIcon,
  MegaphoneIcon,
  MessagesSquareIcon,
  OctagonIcon,
  PencilIcon,
  PhoneIcon,
  PhoneMissedIcon,
  ShieldAlertIcon,
  ShieldCheckIcon,
  TargetIcon,
  UserPlusIcon,
  XCircleIcon,
  type LucideIcon,
} from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link } from 'react-router';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import TimestampTooltip from '@/components/atoms/TimestampTooltip/TimestampTooltip';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { selectAccountSlug } from '@/redux/session/selectors';
import type { NBAAction } from '@/shared/types/campaigns';
import type {
  ContactActivity,
  ContactActivityType,
  DebtPaymentMethod,
  DebtStatus,
} from '@/shared/types/contacts';
import { IntegrationType } from '@/shared/types/integration';
import { getChannelDisplayName } from '@/util/channelName';
import { formatSecondsToMmSs, timeFromNow } from '@/util/dateUtils';

import { DEBT_STATUS_KEY, PAYMENT_METHOD_KEY } from '../constants';
import { contactFieldLabel, formatContactFieldValue } from '../utils/contactFields';

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;
const MONTH_MS = 30 * DAY_MS;
const YEAR_MS = 365 * DAY_MS;

// Custom short formatter that prefers "Nd ago" up to ~30 days (skipping the
// rounded "1w ago" bucket that the default project humanizer falls into).
export const formatActivityTime = (timestamp: string, t: TFunction): string => {
  const ms = Date.now() - new Date(timestamp).getTime();
  if (Number.isNaN(ms) || ms < 0) {
    return '';
  }
  if (ms < MINUTE_MS) {
    return t('common.relative_time.just_now');
  }
  const suffix = t('common.relative_time.ago_short');
  if (ms < HOUR_MS) {
    return `${Math.floor(ms / MINUTE_MS)}m ${suffix}`;
  }
  if (ms < DAY_MS) {
    return `${Math.floor(ms / HOUR_MS)}h ${suffix}`;
  }
  if (ms < MONTH_MS) {
    return `${Math.floor(ms / DAY_MS)}d ${suffix}`;
  }
  if (ms < YEAR_MS) {
    return `${Math.floor(ms / MONTH_MS)}mo ${suffix}`;
  }
  return `${Math.floor(ms / YEAR_MS)}y ${suffix}`;
};

const getInitials = (name?: string): string => {
  if (!name) {
    return '?';
  }
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) {
    return parts[0].charAt(0).toUpperCase();
  }
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

// Voice integrations get call-style labels; every other channel is treated as
// chat. Built from IntegrationType (plus the orchestrator's generic 'voice')
// so a newly added voice channel can't silently fall into the chat branch.
const VOICE_CHANNELS = new Set<string>([
  IntegrationType.TWILIO_VOICE,
  IntegrationType.TELNYX_VOICE,
  IntegrationType.WEB_VOICE,
  'voice',
]);

const conversationDurationMs = (payload: Record<string, unknown>): number | null => {
  const start = typeof payload.start_time === 'string' ? Date.parse(payload.start_time) : NaN;
  const end = typeof payload.end_time === 'string' ? Date.parse(payload.end_time) : NaN;
  if (Number.isNaN(start) || Number.isNaN(end) || end <= start) {
    return null;
  }
  return end - start;
};

interface ActivityRowProps {
  activity: ContactActivity;
  contactName?: string;
  // Whether to render the "next: in X" projection (the row shows regardless). Defaults to true.
  showProjection?: boolean;
  // Opens the contact as it was at this row's time. The button is hidden when absent.
  onShowAsOf?: (at: string) => void;
}

interface TypeStyle {
  Icon: LucideIcon;
  circleClass: string;
}

const NEUTRAL_CIRCLE = 'bg-muted/50 text-muted-foreground';

const getNbaAction = (activity: ContactActivity): NBAAction | undefined => {
  if (activity.type !== 'nba_evaluation') {
    return undefined;
  }
  return (activity.payload?.data as { action?: NBAAction } | undefined)?.action;
};

const NBA_LABEL_STYLE: Record<NBAAction['label'], TypeStyle> = {
  follow_up_now: { Icon: ArrowRightIcon, circleClass: 'bg-info-soft text-foreground' },
  follow_up_later: { Icon: ClockIcon, circleClass: 'bg-warning/10 text-warning' },
  stop: { Icon: OctagonIcon, circleClass: 'bg-destructive/10 text-destructive' },
};

const TYPE_STYLE: Record<ContactActivityType, TypeStyle> = {
  memory_extracted: { Icon: BrainIcon, circleClass: 'bg-ai/10 text-ai' },
  conversation: { Icon: MessagesSquareIcon, circleClass: NEUTRAL_CIRCLE },
  campaign_enrolled: { Icon: MegaphoneIcon, circleClass: 'bg-success/10 text-success' },
  nba_evaluation: { Icon: TargetIcon, circleClass: NEUTRAL_CIRCLE },
  compliance_discovered: { Icon: ShieldAlertIcon, circleClass: 'bg-warning/10 text-warning' },
  contact_created: { Icon: UserPlusIcon, circleClass: NEUTRAL_CIRCLE },
  contact_updated: { Icon: PencilIcon, circleClass: NEUTRAL_CIRCLE },
  deceased_set: { Icon: CircleAlertIcon, circleClass: 'bg-warning/10 text-warning' },
  identity_added: { Icon: AtSignIcon, circleClass: NEUTRAL_CIRCLE },
  identity_verified: { Icon: ShieldCheckIcon, circleClass: 'bg-success/10 text-success' },
  identity_wrong_party: { Icon: ShieldAlertIcon, circleClass: 'bg-warning/10 text-warning' },
  consent_granted: { Icon: CheckCircle2Icon, circleClass: 'bg-success/10 text-success' },
  consent_revoked: { Icon: XCircleIcon, circleClass: 'bg-warning/10 text-warning' },
  debt_status_changed: { Icon: BanknoteIcon, circleClass: NEUTRAL_CIRCLE },
};

const getConversationChannel = (activity: ContactActivity): string => {
  const fromSummary = activity.summary?.values?.channel;
  if (typeof fromSummary === 'string') {
    return fromSummary;
  }
  return typeof activity.payload?.channel === 'string' ? activity.payload.channel : '';
};

// The contact's own message count. Prefer payload.total_user_messages (the
// number of messages the *contact* sent — 0 means no reply / unanswered call)
// and fall back to the summary's message_count for older payloads.
const getConversationMessageCount = (activity: ContactActivity): number => {
  const fromPayload = activity.payload?.total_user_messages;
  if (typeof fromPayload === 'number') {
    return fromPayload;
  }
  return Number(activity.summary?.values?.message_count ?? 0);
};

// Voice conversations get a phone icon; unanswered attempts get a missed-call
// icon with a warning tint so they read as "no contact made".
const conversationStyle = (activity: ContactActivity): TypeStyle | undefined => {
  if (activity.type !== 'conversation' || !VOICE_CHANNELS.has(getConversationChannel(activity))) {
    return undefined;
  }
  return getConversationMessageCount(activity) > 0
    ? { Icon: PhoneIcon, circleClass: NEUTRAL_CIRCLE }
    : { Icon: PhoneMissedIcon, circleClass: 'bg-warning/10 text-warning' };
};

const CATEGORY_KEY: Record<string, string> = {
  fact: 'contacts.fact',
  preference: 'contacts.preference',
};

const IDENTITY_TYPE_KEY: Record<string, string> = {
  phone: 'contacts.phone',
  email: 'contacts.email',
};

const TRANS_COMPONENTS = {
  bold: <strong className="font-medium text-foreground" />,
  accent: <span className="font-medium text-ai" />,
  link: <span className="font-medium text-success underline decoration-dotted" />,
};

const renderIdentityBody = (
  type: ContactActivityType,
  t: TFunction,
  values: Record<string, string | number>,
  payload: Record<string, unknown>
): ReactNode => {
  const identityType = String(values.identity_type ?? '');
  const labelKey = IDENTITY_TYPE_KEY[identityType];
  const label = labelKey ? t(labelKey) : identityType;
  const value = typeof payload?.value === 'string' ? payload.value : '';
  if (type === 'identity_added') {
    return (
      <Trans
        i18nKey="contacts.activity_identity_added"
        values={{ 0: label, 1: value }}
        components={TRANS_COMPONENTS}
      />
    );
  }
  if (type === 'identity_verified') {
    return (
      <Trans
        i18nKey="contacts.activity_identity_verified"
        values={{ 0: label, 1: value }}
        components={TRANS_COMPONENTS}
      />
    );
  }
  return (
    <Trans
      i18nKey="contacts.activity_identity_wrong_party"
      values={{ 0: label, 1: value }}
      components={TRANS_COMPONENTS}
    />
  );
};

const debtStatusLabel = (status: string | number | undefined, t: TFunction): string => {
  const key = DEBT_STATUS_KEY[status as DebtStatus];
  return key ? t(key) : String(status ?? '');
};

const renderDebtStatusBody = (
  t: TFunction,
  { actor, payload, summary }: ContactActivity
): ReactNode => {
  const values = summary?.values ?? {};
  const status = debtStatusLabel(values.status, t);
  const methodKey = PAYMENT_METHOD_KEY[payload?.payment_method as DebtPaymentMethod];
  const method = methodKey ? (
    <span className="text-muted-foreground">
      {' · '}
      {t(methodKey)}
    </span>
  ) : null;
  if (!values.previous_status || values.previous_status === 'none') {
    return (
      <>
        <Trans
          i18nKey="contacts.activity_debt_created"
          values={{ 0: status }}
          components={TRANS_COMPONENTS}
        />
        {method}
      </>
    );
  }
  const previous = debtStatusLabel(values.previous_status, t);
  if (actor?.kind === 'user' && actor.name) {
    return (
      <>
        <Trans
          i18nKey="contacts.activity_debt_status_changed_user"
          values={{ 0: actor.name, 1: previous, 2: status }}
          components={TRANS_COMPONENTS}
        />
        {method}
      </>
    );
  }
  return (
    <>
      <Trans
        i18nKey="contacts.activity_debt_status_changed"
        values={{ 0: previous, 1: status }}
        components={TRANS_COMPONENTS}
      />
      {method}
    </>
  );
};

const renderBody = (
  activity: ContactActivity,
  t: TFunction,
  contactName: string | undefined,
  nba?: NBAAction
): ReactNode => {
  const { type, actor, payload, summary } = activity;
  const values = summary?.values ?? {};

  if (type === 'memory_extracted') {
    const category = String(values.category ?? '');
    const categoryKey = CATEGORY_KEY[category];
    const categoryLabel = categoryKey ? t(categoryKey) : category;
    return (
      <Trans
        i18nKey="contacts.activity_memory_extracted"
        values={{ 0: categoryLabel }}
        components={TRANS_COMPONENTS}
      />
    );
  }
  if (type === 'conversation') {
    // A conversation activity records a session on a channel. The payload has
    // no direction, so we never claim the contact "replied"/"reached out".
    // message_count is the contact's own message count: 0 means they never
    // engaged (unanswered call / no reply) — we still show those so the
    // follow-up activity around them has context.
    const channel = getConversationChannel(activity);
    const messageCount = getConversationMessageCount(activity);

    if (VOICE_CHANNELS.has(channel)) {
      if (messageCount === 0) {
        return t('contacts.activity_call_no_answer');
      }
      const durationMs = conversationDurationMs(payload ?? {});
      const duration = durationMs ? formatSecondsToMmSs(durationMs / 1000) : null;
      const durationSuffix = duration ? ` ${duration}` : '';
      const name = actor?.name || contactName;
      // "{name} answered call 00:32" — only the name is bold.
      if (name) {
        return (
          <>
            <Trans
              i18nKey="contacts.activity_call_answered_by"
              values={{ 0: name }}
              components={TRANS_COMPONENTS}
            />
            {durationSuffix}
          </>
        );
      }
      return (
        <>
          {t('contacts.activity_call_answered')}
          {durationSuffix}
        </>
      );
    }

    if (!channel) {
      return t('contacts.activity_conversation_generic');
    }

    // Chat channels. 0 contact messages = the message was sent but never
    // replied to — the truthful version of the old, wrong "replied" (MOV-16481).
    if (messageCount === 0) {
      return (
        <Trans
          i18nKey="contacts.activity_conversation_no_reply"
          values={{ 0: getChannelDisplayName(channel) }}
          components={TRANS_COMPONENTS}
        />
      );
    }
    return (
      <>
        <Trans
          i18nKey="contacts.activity_conversation_channel"
          values={{ 0: getChannelDisplayName(channel) }}
          components={TRANS_COMPONENTS}
        />
        <span className="text-muted-foreground">
          {' · '}
          {t('contacts.activity_conversation_messages', { count: messageCount })}
        </span>
      </>
    );
  }
  if (type === 'campaign_enrolled') {
    const campaignName = typeof payload?.campaign_name === 'string' ? payload.campaign_name : '';
    return (
      <Trans
        i18nKey="contacts.activity_campaign_enrolled"
        values={{ 0: campaignName }}
        components={TRANS_COMPONENTS}
      />
    );
  }
  if (type === 'nba_evaluation') {
    if (nba?.label === 'follow_up_now') {
      const channelName = nba.channel ? getChannelDisplayName(nba.channel) : undefined;
      return channelName
        ? t('contacts.activity_nba_follow_up_now', { 0: channelName })
        : t('contacts.activity_nba_follow_up_now_generic');
    }
    if (nba?.label === 'follow_up_later') {
      return t('contacts.activity_nba_follow_up_later');
    }
    if (nba?.label === 'stop') {
      return t('contacts.activity_nba_stop');
    }
    return t('contacts.activity_nba_evaluation');
  }
  if (type === 'compliance_discovered') {
    return (
      <Trans
        i18nKey="contacts.activity_compliance_discovered"
        values={{ 0: String(values.flag ?? '') }}
        components={TRANS_COMPONENTS}
      />
    );
  }
  if (type === 'contact_created') {
    if (actor?.kind === 'user') {
      return (
        <Trans
          i18nKey="contacts.activity_contact_created_user"
          values={{ 0: actor.name ?? '' }}
          components={TRANS_COMPONENTS}
        />
      );
    }
    return t('contacts.activity_contact_created_system');
  }
  if (type === 'contact_updated') {
    if (typeof values.field === 'string') {
      const field = contactFieldLabel(values.field, t);
      if (actor?.kind === 'user') {
        return (
          <Trans
            i18nKey="contacts.activity_contact_field_updated_user"
            values={{ 0: actor.name ?? '', 1: field }}
            components={TRANS_COMPONENTS}
          />
        );
      }
      return (
        <Trans
          i18nKey="contacts.activity_contact_field_updated"
          values={{ 0: field }}
          components={TRANS_COMPONENTS}
        />
      );
    }
    if (actor?.kind === 'user') {
      return (
        <Trans
          i18nKey="contacts.activity_contact_updated_user"
          values={{ 0: actor.name ?? '' }}
          components={TRANS_COMPONENTS}
        />
      );
    }
    return t('contacts.activity_contact_updated');
  }
  if (type === 'deceased_set' && payload?.value === false) {
    if (actor?.kind === 'user') {
      return (
        <Trans
          i18nKey="contacts.activity_deceased_unset_user"
          values={{ 0: actor.name ?? '' }}
          components={TRANS_COMPONENTS}
        />
      );
    }
    return t('contacts.activity_deceased_unset');
  }
  if (type === 'deceased_set') {
    if (actor?.kind === 'user') {
      return (
        <Trans
          i18nKey="contacts.activity_deceased_set_user"
          values={{ 0: actor.name ?? '' }}
          components={TRANS_COMPONENTS}
        />
      );
    }
    return t('contacts.activity_deceased_set');
  }
  if (
    type === 'identity_added' ||
    type === 'identity_verified' ||
    type === 'identity_wrong_party'
  ) {
    return renderIdentityBody(type, t, values, payload ?? {});
  }
  if (type === 'consent_granted') {
    return t('contacts.activity_consent_granted');
  }
  if (type === 'consent_revoked') {
    return t('contacts.activity_consent_revoked');
  }
  if (type === 'debt_status_changed') {
    return renderDebtStatusBody(t, activity);
  }
  return null;
};

// A field change from contact_events carries both values; older audit rows carry neither.
const getFieldChange = (
  activity: ContactActivity,
  t: TFunction
): { from: string; to: string } | undefined => {
  const { payload } = activity;
  if (activity.type !== 'contact_updated' || !payload || !('previous_value' in payload)) {
    return undefined;
  }
  return {
    from: formatContactFieldValue(payload.previous_value, t),
    to: formatContactFieldValue(payload.value, t),
  };
};

const getSnippet = (activity: ContactActivity): string | undefined => {
  if (activity.type === 'memory_extracted' && typeof activity.payload?.content === 'string') {
    return activity.payload.content;
  }
  if (activity.type === 'conversation' && typeof activity.payload?.text === 'string') {
    return activity.payload.text;
  }
  return undefined;
};

export const ActivityRow = ({
  activity,
  contactName,
  showProjection = true,
  onShowAsOf,
}: ActivityRowProps) => {
  const { t } = useTranslation();
  const slug = useSelector(selectAccountSlug);
  const nba = getNbaAction(activity);
  const style =
    (nba && NBA_LABEL_STYLE[nba.label]) ?? conversationStyle(activity) ?? TYPE_STYLE[activity.type];
  const body = renderBody(activity, t, contactName, nba);
  const snippet = getSnippet(activity);
  const change = getFieldChange(activity, t);

  if (!body) {
    return null;
  }

  const Icon = style.Icon;
  const showAvatar = activity.actor?.kind === 'user' && !!activity.actor.avatar;
  // Conversation and memory_extracted rows with content deep-link to the session replay.
  // Unanswered calls / unreplied messages have nothing to open.
  const sessionId =
    typeof activity.links?.session_id === 'string' ? activity.links.session_id : undefined;
  const sessionUrl =
    slug &&
    sessionId &&
    ((activity.type === 'conversation' && getConversationMessageCount(activity) > 0) ||
      activity.type === 'memory_extracted')
      ? `/${slug}/analytics/account-chat-logs?session_id=${sessionId}`
      : null;
  // Skip past schedules and ones no longer live.
  const upcomingScheduleMs =
    showProjection &&
    nba?.label === 'follow_up_later' &&
    typeof nba.schedule_at === 'number' &&
    nba.schedule_at > Date.now()
      ? nba.schedule_at
      : undefined;

  return (
    <div className="group flex items-start gap-3">
      <span className="w-14 shrink-0 text-xs leading-6 text-muted-foreground">
        {formatActivityTime(activity.timestamp, t)}
      </span>
      {showAvatar ? (
        <Avatar size="xs" className="size-6 shrink-0">
          <AvatarImage src={activity.actor.avatar} alt={activity.actor.name ?? ''} />
          <AvatarFallback size="xs">{getInitials(activity.actor.name)}</AvatarFallback>
        </Avatar>
      ) : (
        <div
          className={cn(
            'flex size-6 shrink-0 items-center justify-center rounded-full',
            style.circleClass
          )}
        >
          <Icon size={14} />
        </div>
      )}
      <span className="min-w-0 flex-1 text-sm leading-6 break-words text-foreground">
        {sessionUrl ? (
          <Link
            to={sessionUrl}
            target="_blank"
            rel="noreferrer"
            className="transition-colors hover:underline"
          >
            {body}
          </Link>
        ) : (
          body
        )}
        {snippet && <span className="text-muted-foreground">: &ldquo;{snippet}&rdquo;</span>}
        {change && (
          <span className="text-muted-foreground">
            : {change.from} &rarr; {change.to}
          </span>
        )}
        {upcomingScheduleMs && (
          <TimestampTooltip timestamp={upcomingScheduleMs}>
            <span className="text-muted-foreground">
              {' · '}
              {t('contacts.activity_nba_next_in', { 0: timeFromNow(upcomingScheduleMs) })}
            </span>
          </TimestampTooltip>
        )}
        {nba?.reason && (
          <InfoTooltip content={nba.reason} size="large" side="bottom">
            <InfoIcon
              size={14}
              className="ml-1 inline-block align-text-bottom text-muted-foreground"
            />
          </InfoTooltip>
        )}
      </span>
      {onShowAsOf && activity.type !== 'contact_created' && (
        <InfoTooltip content={t('contacts.as_of.open')} side="left">
          <Button
            variant="ghost-neutral"
            size="icon-xs"
            type="button"
            onClick={() => onShowAsOf(activity.timestamp)}
            aria-label={t('contacts.as_of.open')}
            className="mt-0.5 shrink-0 group-hover:opacity-100 focus-visible:opacity-100 lg:opacity-0"
          >
            <HistoryIcon />
          </Button>
        </InfoTooltip>
      )}
    </div>
  );
};
